import { type UniqueRadioButtonValues } from '#/types/inputs';
import otpUiConfig from '#/otp-ui-config';
import {
  Mode,
  OptimizeType,
  type PlanQueryVariables,
  type TransportMode,
} from '#/types/otp2types.generated';
import featureFlags from '#/shared/feature-flags';

const { regionalAgencies } = featureFlags;

export const {
  modes: { transitModes },
  transitOperators,
} = otpUiConfig;

export const VehicleRentalNetworkEnum = Object.fromEntries(
  otpUiConfig.otp2ApiCompanies.map(company => [company.id, company.id])
) as Record<string, string>;

export const scooterCompanies = otpUiConfig.otp2ApiCompanies
  .filter(company => company.modes.split(',').includes(Mode.Scooter))
  .map(company => VehicleRentalNetworkEnum[company.id]);

export const bikeCompanies = otpUiConfig.otp2ApiCompanies
  .filter(company => company.modes.split(',').includes(Mode.Bicycle))
  .map(company => VehicleRentalNetworkEnum[company.id]);

// Define what modes are available
export const tripModeConfig: UniqueRadioButtonValues[] = [
  {
    label: { stringId: 'planner.modesettings-type-transit' },
    value: Mode.Transit,
    dataGtm: 'transit-option',
    description: { stringId: 'planner.modesettings-type-description-transit' },
  },
  {
    label: { stringId: 'planner.modesettings-type-bicycle' },
    value: Mode.Bicycle,
    dataGtm: 'bicycle-option',
    description: { stringId: 'planner.modesettings-type-description-bicycle' },
  },
  {
    label: { stringId: 'planner.modesettings-type-scooter' },
    value: Mode.Scooter,
    dataGtm: 'scooter-option',
    description: { stringId: 'planner.modesettings-type-description-scooter' },
  },
  {
    label: { stringId: 'planner.modesettings-type-car' },
    value: Mode.Car,
    dataGtm: 'car-option',
    description: { stringId: 'planner.modesettings-type-description-car' },
  },
];

// Extract the `value` properties from `tripModeConfig` as a union type
type TripModeValues = (typeof tripModeConfig)[number]['value'];

// Define the type for modeOptionsConfig
export type ModeOptionsConfig = Record<
  TripModeValues,
  {
    checkBox?: (keyof CheckConfig)[];
    select?: (keyof SelectConfig)[];
  }
>;

// Define the type for selectConfig
export type SelectConfig = Record<
  string,
  {
    labelId: string;
    name: string;
    options: { value: number | string; labelId: string }[];
  }
>;

// Define the type for checkConfig
export type CheckConfig = Record<
  string,
  {
    labelId: string;
    name: string;
    value: string;
  }
>;

// Define what options should show for each mode
export const modeOptionsConfig: ModeOptionsConfig = {
  [Mode.Transit]: { select: ['walkReluctance', 'walkSpeed'] },
  [Mode.Bicycle]: {
    checkBox: ['bikeBiketown'],
    select: [
      'walkReluctance',
      'walkSpeed',
      'bikeReluctance',
      'bikeSpeed',
      'optimize',
    ],
  },
  [Mode.Scooter]: {
    checkBox: ['scooterLime', 'scooterBiketown'],
    select: ['walkReluctance', 'walkSpeed', 'scooterReluctance'],
  },
  [Mode.Car]: { select: ['walkReluctance', 'walkSpeed', 'carReluctance'] },
};

// Define options with their labels and values
export const selectConfig: SelectConfig = {
  walkReluctance: {
    labelId: 'planner.preferences-walkreluctance-label',
    name: 'walkReluctance',
    options: [
      {
        value: 2,
        labelId: 'planner.preferences-walkreluctance-low',
      },
      {
        value: 4,
        labelId: 'planner.preferences-walkreluctance-normal',
      },
      {
        value: 6,
        labelId: 'planner.preferences-walkreluctance-high',
      },
    ],
  },
  walkSpeed: {
    labelId: 'planner.preferences-walkspeed-label',
    name: 'walkSpeed',
    // Value is in meters per second
    // 1 mph = 0.447 m/s
    options: [
      { value: 0.89, labelId: 'planner.preferences-walkspeed-2mph' },
      { value: 1.34, labelId: 'planner.preferences-walkspeed-3mph' },
      { value: 1.79, labelId: 'planner.preferences-walkspeed-4mph' },
    ],
  },
  bikeReluctance: {
    labelId: 'planner.preferences-bikereluctance-label',
    name: 'bikeReluctance',
    options: [
      { value: 2, labelId: 'planner.preferences-bikereluctance-low' },
      { value: 4, labelId: 'planner.preferences-bikereluctance-normal' },
      { value: 6, labelId: 'planner.preferences-bikereluctance-high' },
    ],
  },
  // Scooter reluctance is the same as bike reluctance
  // Plan query does not have a separate scooter reluctance
  // but we need to show it in the UI and PlanConnection query
  // does have a separate scooter reluctance
  scooterReluctance: {
    labelId: 'planner.preferences-scooterreluctance-label',
    name: 'bikeReluctance',
    options: [
      { value: 2, labelId: 'planner.preferences-scooterreluctance-low' },
      { value: 4, labelId: 'planner.preferences-scooterreluctance-normal' },
      { value: 6, labelId: 'planner.preferences-scooterreluctance-high' },
    ],
  },
  carReluctance: {
    labelId: 'planner.preferences-carreluctance-label',
    name: 'carReluctance',
    options: [
      { value: 2, labelId: 'planner.preferences-carreluctance-low' },
      { value: 25, labelId: 'planner.preferences-carreluctance-normal' },
      { value: 100, labelId: 'planner.preferences-carreluctance-high' },
    ],
  },
  bikeSpeed: {
    labelId: 'planner.preferences-bikespeed-label',
    name: 'bikeSpeed',
    // Value is in meters per second
    // 1 mph = 0.447 m/s
    options: [
      { value: 2.68, labelId: 'planner.preferences-bikespeed-6mph' },
      { value: 3.58, labelId: 'planner.preferences-bikespeed-8mph' },
      { value: 4.47, labelId: 'planner.preferences-bikespeed-10mph' },
      { value: 5.36, labelId: 'planner.preferences-bikespeed-12mph' },
    ],
  },
  optimize: {
    labelId: 'planner.preferences-optimize-label',
    name: 'optimize',
    options: [
      {
        value: OptimizeType.Flat,
        labelId: 'planner.preferences-optimize-flat',
      },
      {
        value: OptimizeType.Quick,
        labelId: 'planner.preferences-optimize-fast',
      },
      {
        value: OptimizeType.Safe,
        labelId: 'planner.preferences-optimize-safe',
      },
    ],
  },
} as const;

// Define options with their labels and values
export const checkBoxConfig: CheckConfig = {
  scooterLime: {
    labelId: 'planner.preferences-scooter-lime',
    name: 'allowedVehicleRentalNetworks',
    value: VehicleRentalNetworkEnum.LIME,
  },
  scooterBiketown: {
    labelId: 'planner.preferences-scooter-biketown',
    name: 'allowedVehicleRentalNetworks',
    value: VehicleRentalNetworkEnum.lyft_pdx,
  },
  bikeBiketown: {
    labelId: 'planner.preferences-bike-biketown',
    name: 'allowedVehicleRentalNetworks',
    value: VehicleRentalNetworkEnum.lyft_pdx,
  },
} as const;

/**
 * Default transport modes - single source of truth for all default modes
 */
export const defaultTransportModes: readonly TransportMode[] = transitModes
  .filter(mode => regionalAgencies || mode.mode !== Mode.Flex)
  .flatMap(mode => {
    // Check if mode has qualifiers defined
    if (mode.qualifiers && mode.qualifiers.length > 0) {
      // Create multiple entries with different qualifiers
      return mode.qualifiers.map(qualifier => ({
        mode: mode.mode,
        qualifier,
      }));
    }

    // Regular mode handling
    return [{ mode: mode.mode }];
  });

/**
 * Gets all transport modes for a specific mode type from the default set
 */
export function getTransportModesForMode(mode: Mode): TransportMode[] {
  return defaultTransportModes.filter(tm => tm.mode === mode);
}

export const otp2DefaultQuery: PlanQueryVariables = {
  allowedVehicleRentalNetworks: [],
  arriveBy: false,
  banned: {},
  bikeReluctance: 4, // Normal reluctance for bicycle
  bikeSpeed: 3.58,
  carReluctance: 25, // Normal reluctance for car
  date: '',
  fromPlace: '',
  modes: [...defaultTransportModes],
  searchWindow: 14400,
  time: '',
  toPlace: '',
  optimize: OptimizeType.Quick,
  walkReluctance: 4, // Normal reluctance for walking
  walkSpeed: 1.34,
};
